import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/Dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { Input } from '../../../components/ui/Input';
import { useServices } from '../../../core/hooks/useServices';
import { useAuth } from '../../../core/contexts/AuthContext';
import { EmptyState } from '../../../components/shared/EmptyState';

interface ClientServicesTabProps {
  clientId: string;
}

interface ServiceAssignment {
  serviceId: string;
  serviceName: string;
  rate: number;
  unit: string;
  assignedAt: Date;
}

export const ClientServicesTab: React.FC<ClientServicesTabProps> = () => {
  const { organizationId } = useAuth();
  const { services } = useServices(organizationId);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [customRate, setCustomRate] = useState<string>('');
  
  // Mock assigned services - replace with actual data from Firebase
  const [assignedServices, setAssignedServices] = useState<ServiceAssignment[]>([]);

  const handleAddService = () => {
    const selectedService = services.find(s => s.id === selectedServiceId);
    if (selectedService) {
      const newAssignment: ServiceAssignment = {
        serviceId: selectedService.id,
        serviceName: selectedService.name,
        rate: customRate ? parseFloat(customRate) : selectedService.defaultRate,
        unit: selectedService.unit,
        assignedAt: new Date(),
      };
      setAssignedServices([...assignedServices, newAssignment]);
      setIsAddDialogOpen(false);
      setSelectedServiceId('');
      setCustomRate('');
    }
  };

  const handleRemoveService = (serviceId: string) => {
    setAssignedServices(assignedServices.filter(s => s.serviceId !== serviceId));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Assigned Services</h3>
        <Button onClick={() => setIsAddDialogOpen(true)} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Service
        </Button>
      </div>

      {assignedServices.length === 0 ? (
        <EmptyState
          title="No services assigned"
          description="Assign services to this project to start tracking work"
          action={{
            label: 'Add Service',
            onClick: () => setIsAddDialogOpen(true),
          }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {assignedServices.map((assignment) => (
            <Card key={assignment.serviceId}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-base">{assignment.serviceName}</CardTitle>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveService(assignment.serviceId)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Rate:</span>
                    <span className="font-medium">${assignment.rate}/{assignment.unit}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Assigned:</span>
                    <span>{assignment.assignedAt.toLocaleDateString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Service Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Service</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Service</label>
              <Select value={selectedServiceId} onValueChange={setSelectedServiceId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent>
                  {services.filter(s => s.isActive).map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.name} - ${service.defaultRate}/{service.unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedServiceId && (
              <Input
                label="Custom Rate (Optional)"
                type="number"
                placeholder={`Default: ${services.find(s => s.id === selectedServiceId)?.defaultRate}`}
                value={customRate}
                onChange={(e) => setCustomRate(e.target.value)}
              />
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddService} disabled={!selectedServiceId}>
                Add Service
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
